import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X, ArrowRight } from "lucide-react";
import { NAV } from "@/data/site";
import { Cta } from "@/components/Cta";
import { EASE } from "@/components/motion";
import { lenisRef } from "@/lib/lenis";

export function Logo({ light = false, className = "h-9 sm:h-10" }) {
  return (
    <img src={light ? "/brand/logo-on-light.webp" : "/brand/logo-on-dark.webp"} alt="SparkIdea Interactive"
      width="640" height="224" className={`${className} w-auto select-none`} draggable="false" />
  );
}

function Dropdown({ item, open, setOpen, active }) {
  const id = `nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <div className="relative" onMouseEnter={() => setOpen(item.label)} onMouseLeave={() => setOpen(null)}>
      <button type="button" aria-expanded={open} aria-controls={`${id}-menu`} data-testid={`${id}-trigger`}
        onClick={() => setOpen(open ? null : item.label)}
        className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-[15px] font-semibold transition-colors duration-200 ${active ? "text-si-text" : "text-si-muted hover:text-si-text"}`}>
        {item.label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        {active && <span className="absolute -bottom-0.5 left-3 right-3 h-px bg-si-cyan" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div id={`${id}-menu`} initial={{ opacity: 0, y: 8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6 }} transition={{ duration: 0.22, ease: EASE }}
            className="absolute left-1/2 top-full z-50 w-[340px] -translate-x-1/2 pt-3">
            <div className="overflow-hidden rounded-2xl border border-si-line bg-[#0A1020]/95 p-2 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] backdrop-blur-xl">
              {item.items.map((s) => (
                <Link key={s.to + s.label} to={s.to} onClick={() => setOpen(null)} data-testid={`${id}-link-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group flex items-center justify-between rounded-xl px-4 py-3 transition-colors duration-200 hover:bg-si-elev/80 focus-visible:bg-si-elev/80">
                  <span>
                    <span className="block text-[15px] font-semibold text-si-text">{s.label}</span>
                    <span className="block text-[13px] text-si-muted">{s.desc}</span>
                  </span>
                  <ArrowRight className="h-4 w-4 -translate-x-1 text-si-cyan opacity-0 transition-[opacity,transform] duration-200 group-hover:translate-x-0 group-hover:opacity-100" />
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MobileMenu({ onClose }) {
  const [expanded, setExpanded] = useState(null);
  const panelRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    lenisRef.current?.stop();
    panelRef.current?.querySelector("a,button")?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      lenisRef.current?.start();
    };
  }, [onClose]);
  return (
    <motion.div ref={panelRef} role="dialog" aria-modal="true" aria-label="Site navigation" data-testid="mobile-nav-panel"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[70] overflow-y-auto bg-si-bg backdrop-blur-xl lg:hidden" data-lenis-prevent>
      <div className="wrap flex h-20 items-center justify-between">
        <Link to="/" onClick={onClose} aria-label="Home"><Logo className="h-8" /></Link>
        <button type="button" onClick={onClose} aria-label="Close menu" data-testid="mobile-nav-close"
          className="grid h-11 w-11 place-items-center rounded-xl border border-si-line"><X className="h-5 w-5" /></button>
      </div>
      <nav className="wrap pb-10 pt-4">
        {NAV.map((item, i) => (
          <motion.div key={item.label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 + i * 0.05, duration: 0.5, ease: EASE }} className="border-b border-si-line">
            {item.items ? (
              <>
                <button type="button" aria-expanded={expanded === item.label} data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setExpanded(expanded === item.label ? null : item.label)}
                  className="flex w-full items-center justify-between py-5 text-2xl font-bold">
                  {item.label}<ChevronDown className={`h-5 w-5 text-si-cyan transition-transform ${expanded === item.label ? "rotate-180" : ""}`} />
                </button>
                {expanded === item.label && (
                  <div className="pb-4">
                    {item.items.map((s) => (
                      <Link key={s.to + s.label} to={s.to} onClick={onClose} className="block py-2.5 pl-2 text-base text-si-muted hover:text-si-text">{s.label}</Link>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <Link to={item.to} onClick={onClose} data-testid={`mobile-nav-${item.label.toLowerCase()}`} className="block py-5 text-2xl font-bold">{item.label}</Link>
            )}
          </motion.div>
        ))}
        <div className="mt-8"><Cta to="/contact" onClick={onClose} className="w-full" testId="mobile-nav-cta">Start a Project</Cta></div>
      </nav>
    </motion.div>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(null);
  const [mobile, setMobile] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(null); setMobile(false); }, [pathname]);
  const isActive = (item) => pathname === item.to || pathname.startsWith(item.to + "/") || item.items?.some((s) => pathname === s.to);
  return (
    <>
      <header data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,height] duration-300 ${scrolled ? "border-b border-si-line/80 bg-si-bg/75 backdrop-blur-xl" : "border-b border-transparent bg-transparent"}`}>
        <div className={`wrap flex items-center justify-between transition-[height] duration-300 ${scrolled ? "h-16" : "h-20 sm:h-24"}`}>
          <Link to="/" aria-label="SparkIdea Interactive home" data-testid="header-logo"><Logo className={scrolled ? "h-9" : "h-10 sm:h-12"} /></Link>
          <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
            {NAV.map((item) => item.items ? (
              <Dropdown key={item.label} item={item} open={open === item.label} setOpen={setOpen} active={isActive(item)} />
            ) : (
              <NavLink key={item.label} to={item.to} data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive: a }) => `relative rounded-lg px-3 py-2 text-[15px] font-semibold transition-colors duration-200 ${a ? "text-si-text" : "text-si-muted hover:text-si-text"}`}>
                {({ isActive: a }) => (<>{item.label}{a && <span className="absolute -bottom-0.5 left-3 right-3 h-px bg-si-cyan" />}</>)}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Cta to="/contact" className="!hidden sm:!inline-flex !h-11" testId="header-cta" trackId="header-start-project">Start a Project</Cta>
            <button type="button" className="grid h-11 w-11 place-items-center rounded-xl border border-si-line bg-si-surface/50 lg:hidden"
              aria-label="Open menu" aria-expanded={mobile} onClick={() => setMobile(true)} data-testid="mobile-nav-open">
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>{mobile && <MobileMenu onClose={() => setMobile(false)} />}</AnimatePresence>
    </>
  );
}
